import React, { useState } from 'react';
import { groupsAPI, membersAPI } from '../../../services/api';
import EditGroupForm from './EditGroupForm';
import { formatGroupType } from '../../../utils/formatters';
import './GroupCard.css';
import { isAdmin, isSuperAdmin } from '../../../utils/auth-utils';

function GroupCard({ group, isAdmin, isEditor, isSuperAdmin, onUpdate, onDelete }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [memberDetails, setMemberDetails] = useState([]);
    const [showMembers, setShowMembers] = useState(false);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);

    // Safely get the first character of a name
    const getInitial = (name) => {
        return name && name.length > 0 ? name[0] : '?';
    };

    const handleDelete = async () => {
        if (!isSuperAdmin) return;
        
        if (!window.confirm('Are you sure you want to delete this group?')) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await groupsAPI.deleteGroup(group.id);
            onDelete(group.id);
        } catch (error) {
            setError(error.message || 'Failed to delete group');
        } finally {
            setLoading(false);
        }
    };

    const toggleMembers = async () => {
        if (!showMembers && memberDetails.length === 0) {
            // Only fetch members when showing them for the first time
            await fetchMemberDetails();
        }
        
        setShowMembers(!showMembers);
    };

    const fetchMemberDetails = async () => {
        try {
            setIsLoadingMembers(true);
            if (!group.members || group.members.length === 0) {
                setMemberDetails([]);
                return;
            }

            const response = await membersAPI.getMembersByIds(group.members);

            if (response) {
                // Handle both array and object response structures
                const membersData = Array.isArray(response) ? response : [response];
                const members = membersData.map(member => ({
                    id: member.id,
                    name: member.displayName || member.name || 'Unknown Member',
                    email: member.email || 'No email provided',
                    role: member.role || 'member',
                    picture: member.photoURL || member.picture
                }));
                setMemberDetails(members);
            } else {
                setMemberDetails([]);
            }
        } catch (error) {
            setMemberDetails([]);
        } finally {
            setIsLoadingMembers(false);
        }
    };

    const handleGroupUpdated = () => {
        if (onUpdate) {
            onUpdate();
        }
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <EditGroupForm 
                group={group} 
                onClose={() => setIsEditing(false)} 
                onGroupUpdated={handleGroupUpdated} 
            />
        );
    }

    return (
        <div className="group-card">
            {error && <div className="error-message">{error}</div>}
            <div className="group-header">
                {group.logoUrl ? (
                    <img src={group.logoUrl} alt={`${group.name || 'Group'} logo`} className="group-logo" />
                ) : (
                    <div className="group-logo-placeholder">{getInitial(group.name)}</div>
                )}
                <div className="group-info">
                    <h3>{group.name || 'Unnamed Group'}</h3>
                    <p className="group-type">{formatGroupType(group.type)}</p>
                </div>
            </div>
            
            <p className="group-description">{group.description || 'No description available.'}</p>
            
            <div className="group-members">
                <div className="members-header">
                    <h4>Members ({group.members ? group.members.length : 0})</h4>
                    <button className="toggle-members-button" onClick={toggleMembers}>
                        {showMembers ? 'Hide Members' : 'Show Members'}
                        <i className={`fas fa-chevron-${showMembers ? 'up' : 'down'}`}></i>
                    </button>
                </div>
                
                {showMembers && (
                    <div className="members-container">
                        {isLoadingMembers ? (
                            <div className="loading-members">
                                <i className="fas fa-spinner fa-spin"></i> Loading members...
                            </div>
                        ) : memberDetails.length > 0 ? (
                            <div className="members-list">
                                {memberDetails.map(member => (
                                    <div key={member.id || Math.random()} className="member-item">
                                        {member.picture || member.profileImage || member.imageUrl ? (
                                            <img src={member.picture || member.profileImage || member.imageUrl} alt={member.name || 'Member'} className="member-avatar" />
                                        ) : (
                                            <div className="member-avatar-placeholder">{getInitial(member.name)}</div>
                                        )}
                                        <span className="member-name">{member.name || 'Unknown Member'}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="no-members">No members in this group.</p>
                        )}
                    </div>
                )}
            </div>
            
            {group.socialLinks && (
                <div className="group-social">
                    {group.socialLinks.facebook && (
                        <a href={group.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                            <i className="fab fa-facebook"></i>
                        </a>
                    )}
                    {group.socialLinks.instagram && (
                        <a href={group.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                            <i className="fab fa-instagram"></i>
                        </a>
                    )}
                </div>
            )}
            
            {(isAdmin || isEditor) && (
                <div className="group-actions">
                    <button onClick={() => setIsEditing(true)} disabled={loading}>
                        <i className="fas fa-edit"></i> Edit
                    </button>
                    {isSuperAdmin && (
                        <button onClick={handleDelete} disabled={loading} className="delete-button">
                            <i className="fas fa-trash"></i> Delete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default GroupCard;